import { createElement } from 'react'
import {
    FilterModuleConfig,
    transformToGenericFilterConfig,
} from '@core/modules/filters'
import {
    SearchKeyConfig,
    UnifiedFilterConfig,
    UnifiedFilterModule,
} from '@core/modules/filters/unified-filter.module'
import { SeverityIcon } from '@/ui/components/svg/SeverityIcon'
import { getHighestSeverity } from '@routes/digital-footprint/routes/assets/components/assetsTable/utils'
import { TECHNOLOGY_TYPE_LABELS } from './constants'

export const technologiesSearchKeys: SearchKeyConfig[] = [
    { value: 'name', threshold: 0.3 },
    { value: 'type', threshold: 0.2 },
    { value: 'version', threshold: 0.3 },
    { value: 'issues.severity', threshold: 0.2 },
    { value: 'issues.name', threshold: 0.3 },
]

const sourceFilters = Object.entries(TECHNOLOGY_TYPE_LABELS).reduce(
    (acc, [key, label]) => {
        acc[key] = { accessor: key, label }
        return acc
    },
    {} as Record<string, { accessor: string; label: string }>
)

const config = {
    type: {
        accessor: 'type',
        label: 'Source',
        collapsible: true,
        filters: sourceFilters,
    },
    severity: {
        accessor: 'severity',
        label: 'Severity',
        collapsible: true,
        filters: {
            critical: {
                accessor: 'critical',
                label: 'Critical',
                icon: createElement(SeverityIcon, { severity: 'critical' }),
            },
            important: {
                accessor: 'important',
                label: 'Important',
                icon: createElement(SeverityIcon, { severity: 'important' }),
            },
            moderate: {
                accessor: 'moderate',
                label: 'Moderate',
                icon: createElement(SeverityIcon, { severity: 'moderate' }),
            },
            clean: {
                accessor: 'clean',
                label: 'Excellent',
                icon: createElement(SeverityIcon, { severity: 'clean' }),
            },
        },
    },
}

const defaultFilters = {
    type: Object.keys(TECHNOLOGY_TYPE_LABELS).reduce(
        (acc, key) => {
            acc[key] = true
            return acc
        },
        {} as Record<string, boolean>
    ),
    severity: {
        critical: true,
        important: true,
        moderate: true,
        clean: true,
    },
}

export const technologiesFilterConfig: FilterModuleConfig =
    transformToGenericFilterConfig(
        { technologies: config },
        { technologies: defaultFilters }
    ).technologies

export const unifiedTechnologiesConfig: UnifiedFilterConfig = {
    ...technologiesFilterConfig,
    searchKeys: technologiesSearchKeys,
    sortStrategy: (a: TTechnology, b: TTechnology) => {
        const aCount = a.issues?.length ?? 0
        const bCount = b.issues?.length ?? 0
        if (bCount !== aCount) return bCount - aCount
        return (a.name || '').localeCompare(b.name || '')
    },
    dataTransformer: (data: TTechnology[]) =>
        data.map((technology) => ({
            ...technology,
            severity: getHighestSeverity(technology.issues),
        })),
}

export const unifiedTechnologiesFilterModule = new UnifiedFilterModule(
    unifiedTechnologiesConfig,
    'technologies.unified.filter'
)
